import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import UserMenu from "@/components/UserMenu";
import Logo from "@/components/Logo";
import { AnimatePresence } from "framer-motion";
import AuthBottomSheet from "@/components/card/AuthBottomSheet";

interface AppHeaderProps {
  /** Show a back arrow; provide the path or a callback */
  onBack?: (() => void) | string;
  /** Extra elements rendered before the user menu */
  actions?: React.ReactNode;
  /** Use bottom sheet for login instead of navigating to /auth */
  useAuthSheet?: boolean;
  /** Variant for the user menu */
  userMenuVariant?: "default" | "dashboard";
}

const AppHeader = ({ onBack, actions, useAuthSheet, userMenuVariant }: AppHeaderProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showAuthSheet, setShowAuthSheet] = useState(false);

  const handleBack = () => {
    if (typeof onBack === "string") {
      navigate(onBack);
    } else if (onBack) {
      onBack();
    }
  };

  return (
    <>
      <nav className="sticky top-0 z-50 bg-background/90 backdrop-blur-sm border-b border-border">
        <div className="max-w-6xl mx-auto flex items-center justify-between py-3 px-4 sm:px-6">
          <div className="flex items-center gap-2">
            {onBack && (
              <button
                onClick={handleBack}
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft size={18} />
              </button>
            )}
            <button onClick={() => navigate("/")}>
              <Logo />
            </button>
          </div>
          <div className="flex items-center gap-2">
            {actions}
            {user ? (
              <UserMenu variant={userMenuVariant} />
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="rounded-full"
                onClick={() => useAuthSheet ? setShowAuthSheet(true) : navigate("/auth")}
              >
                {t("nav.signIn")}
              </Button>
            )}
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {showAuthSheet && (
          <AuthBottomSheet
            onClose={() => setShowAuthSheet(false)}
            onAuthenticated={() => setShowAuthSheet(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default AppHeader;
